/**
 * Small data layer without TanStack Query: one hook per request with a shared in-memory cache
 * (stale-while-revalidate), abort on unmount, and explicit reload. Cursor pages accumulate.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ApiError, call, type CallArgs, type RouteId, type RouteInput, type RouteOutput, type RouteParams } from './api.js';

const cache = new Map<string, unknown>();

export function cacheKey(id: string, args: unknown): string {
  return `${id}:${JSON.stringify(args ?? {})}`;
}

/** Drop cached entries whose route id starts with any prefix (call after mutations). */
export function invalidate(...prefixes: string[]): void {
  for (const key of Array.from(cache.keys())) {
    if (prefixes.some((p) => key.startsWith(p))) cache.delete(key);
  }
  for (const l of invalidationListeners) l(prefixes);
}
const invalidationListeners = new Set<(prefixes: string[]) => void>();

export interface QueryState<T> {
  data: T | null;
  error: ApiError | null;
  /** True during the first load (no data yet). */
  loading: boolean;
  /** True during any in-flight refresh. */
  refreshing: boolean;
  reload: () => Promise<void>;
  setData: (updater: (prev: T | null) => T | null) => void;
}

export interface UseApiOptions {
  /** Skip the request (e.g. capability missing). */
  enabled?: boolean;
  /** Poll interval in ms. */
  refreshMs?: number;
}

export function useApi<Id extends RouteId>(id: Id, args: Omit<CallArgs<Id>, 'signal'> = {}, options: UseApiOptions = {}): QueryState<RouteOutput<Id>> {
  const enabled = options.enabled ?? true;
  const key = cacheKey(id, args);
  const [data, setDataState] = useState<RouteOutput<Id> | null>(() => (cache.get(key) as RouteOutput<Id> | undefined) ?? null);
  const [error, setError] = useState<ApiError | null>(null);
  const [inflight, setInflight] = useState(enabled);
  const keyRef = useRef(key);
  const argsRef = useRef(args);
  argsRef.current = args;

  const run = useCallback(
    async (signal?: AbortSignal) => {
      if (!enabled) return;
      setInflight(true);
      try {
        const res = await call(id, { ...(argsRef.current as CallArgs<Id>), ...(signal ? { signal } : {}) });
        if (signal?.aborted) return;
        cache.set(key, res.data);
        setDataState(res.data);
        setError(null);
      } catch (e) {
        if (signal?.aborted || (e instanceof DOMException && e.name === 'AbortError')) return;
        setError(ApiError.from(e));
      } finally {
        if (!signal?.aborted) setInflight(false);
      }
    },
    [id, key, enabled],
  );

  useEffect(() => {
    if (keyRef.current !== key) {
      keyRef.current = key;
      const cached = cache.get(key) as RouteOutput<Id> | undefined;
      setDataState(cached ?? null);
      setError(null);
    }
    if (!enabled) {
      setInflight(false);
      return;
    }
    const controller = new AbortController();
    void run(controller.signal);
    const listener = (prefixes: string[]) => {
      if (prefixes.some((p) => id.startsWith(p))) void run(controller.signal);
    };
    invalidationListeners.add(listener);
    let timer: number | undefined;
    if (options.refreshMs) timer = window.setInterval(() => void run(controller.signal), options.refreshMs);
    return () => {
      controller.abort();
      invalidationListeners.delete(listener);
      if (timer) window.clearInterval(timer);
    };
  }, [key, run, enabled, id, options.refreshMs]);

  const setData = useCallback(
    (updater: (prev: RouteOutput<Id> | null) => RouteOutput<Id> | null) => {
      setDataState((prev) => {
        const next = updater(prev);
        if (next === null) cache.delete(key);
        else cache.set(key, next);
        return next;
      });
    },
    [key],
  );

  return useMemo(
    () => ({ data, error, loading: inflight && data === null, refreshing: inflight, reload: () => run(), setData }),
    [data, error, inflight, run, setData],
  );
}

type PageLike<T> = { items: T[]; nextCursor: string | null; total?: number | null };
type ItemOf<Id extends RouteId> = RouteOutput<Id> extends PageLike<infer T> ? T : never;

export interface PagedState<Id extends RouteId> {
  items: ItemOf<Id>[];
  /** Full first page (for extra fields such as summary or counts). */
  first: RouteOutput<Id> | null;
  total: number | null;
  hasMore: boolean;
  loading: boolean;
  loadingMore: boolean;
  error: ApiError | null;
  loadMore: () => Promise<void>;
  reload: () => Promise<void>;
}

/** Cursor pagination: first page via the cache, subsequent pages appended. */
export function usePaged<Id extends RouteId>(id: Id, args: { params?: RouteParams<Id>; input?: RouteInput<Id> } = {}, options: UseApiOptions = {}): PagedState<Id> {
  const first = useApi(id, args, options);
  const [extra, setExtra] = useState<{ key: string; pages: PageLike<ItemOf<Id>>[] }>({ key: '', pages: [] });
  const [loadingMore, setLoadingMore] = useState(false);
  const [moreError, setMoreError] = useState<ApiError | null>(null);
  const key = cacheKey(id, args);
  const pages = extra.key === key ? extra.pages : [];
  const firstPage = first.data as PageLike<ItemOf<Id>> | null;
  const last = pages.length > 0 ? pages[pages.length - 1] : firstPage;
  const hasMore = Boolean(last?.nextCursor);

  const loadMore = useCallback(async () => {
    if (!last?.nextCursor || loadingMore) return;
    setLoadingMore(true);
    try {
      const input = { ...((args.input ?? {}) as Record<string, unknown>), cursor: last.nextCursor } as RouteInput<Id>;
      const res = await call(id, { ...(args.params ? { params: args.params } : {}), input });
      setExtra((prev) => ({ key, pages: [...(prev.key === key ? prev.pages : []), res.data as PageLike<ItemOf<Id>>] }));
      setMoreError(null);
    } catch (e) {
      setMoreError(ApiError.from(e));
    } finally {
      setLoadingMore(false);
    }
  }, [id, key, args.input, args.params, last, loadingMore]);

  const reload = useCallback(async () => {
    setExtra({ key, pages: [] });
    await first.reload();
  }, [first, key]);

  const items = useMemo(() => [...(firstPage?.items ?? []), ...pages.flatMap((p) => p.items)], [firstPage, pages]);

  return {
    items,
    first: first.data,
    total: firstPage?.total ?? null,
    hasMore,
    loading: first.loading,
    loadingMore,
    error: first.error ?? moreError,
    loadMore,
    reload,
  };
}
